import { Check, Languages } from "lucide-react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { Button } from "@holigral/shared/ui/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@holigral/shared/ui/components/ui/dropdown-menu"
import { useUpdateLocaleMutation } from "./api"
import { useLocale } from "@/base"
import { type Locale, SUPPORTED_LOCALES, useT } from "@/i18n"
import { useAuth } from "@/auth"

type Props = {
  /** Trigger button variant; defaults to a quiet ghost button. */
  variant?: "ghost" | "outline"
  align?: "start" | "end"
}

/**
 * Switches the UI language. On public pages the locale lives in the URL, so we
 * navigate to the same route under the new locale (the $locale route applies
 * it). In the app there is no URL locale, so we change it in place. Either way,
 * signed-in users persist the choice via PATCH /users/me/locale so it follows
 * them across devices. Lives in a feature (not shared) because it needs both
 * auth state and the profile mutation.
 */
export default function LanguageSwitcher({
  variant = "ghost",
  align = "end",
}: Props) {
  const { t, i18n } = useT("common")
  const { isAuthenticated } = useAuth()
  const [updateLocale] = useUpdateLocaleMutation()
  const navigate = useNavigate()
  const current = useLocale()
  // Public pages carry the locale in the URL ($locale param); the app does not.
  const onPublicPage = typeof useParams({ strict: false }).locale === "string"

  const select = (locale: Locale) => {
    if (locale === current) {
      return
    }

    if (isAuthenticated) {
      void updateLocale({ locale })
    }

    if (onPublicPage) {
      void navigate({
        to: ".",
        params: (prev) => ({ ...prev, locale }),
        replace: true,
      })
    } else {
      void i18n.changeLanguage(locale)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant={variant} size="sm" aria-label={t("changeLanguage")}>
            <Languages />
            {t(`locales.${current}`)}
          </Button>
        }
      />
      <DropdownMenuContent align={align}>
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => {
              select(locale)
            }}
          >
            <Check
              className={locale === current ? "opacity-100" : "opacity-0"}
            />
            {t(`locales.${locale}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
